import { useState } from 'react'

export interface PartThumbnailsProps {
  /** The order's PARTS count (`OrderSummary.lineCount`) — how many
   * thumbnails (real or "+N" overflow) to represent. AUTHORITATIVE for the
   * "+N" overflow math; `thumbnailUrls.length` is never a parts count (a
   * shorter `thumbnailUrls` — unresolvable ids, null thumbnails, the
   * server's cap of 3 — is the normal case, ADR-0126 D12). */
  readonly count: number
  /** Up to `max` real part-thumbnail URLs. The array is DENSE and
   * FRONT-PACKED, never sparse: the server (`resolveThumbnailUrls`) walks
   * line order but pushes only RESOLVED urls, filtering misses/nulls BEFORE
   * applying its cap — so index `i` here is positionally UNRELATED to part
   * `i` or `partNames[i]`; do not pair them (e.g. for a tooltip or `alt`) on
   * the assumption that this array mirrors line order. A `thumbnailUrls`
   * shorter than the number of shown slots is the NORMAL "mixed row" case —
   * later slots fall back to the decorative cube glyph — never a signal
   * about which specific part lacks an image (UX spec §1.2c). Defaults to
   * `[]` for callers that only ever pass `count` (e.g. `RecentOrders.tsx`,
   * out of this story's scope), which renders exactly the old all-fallback
   * behavior. */
  readonly thumbnailUrls?: readonly string[] | undefined
  /** How many individual swatches to render before folding the remainder
   * into a "+N" overflow chip. Defaults to the design's row treatment
   * (`Fictiv Platform.dc.html` home `ordersTable` ~L465: up to 2 shown
   * before `moreThumbs`). */
  readonly max?: number
}

/**
 * Per-row part thumbnails (UID-132, real images added UID-404/ADR-0126
 * D12/D17). The group stays `aria-hidden` in its ENTIRETY even once it
 * renders real photos (UX spec §1.3): the PARTS cell's adjacent text
 * (`formatPartsSummary`'s count+units line, plus the part-names line) already
 * names every part non-visually, so a per-swatch `alt` would just repeat that
 * signal up to 400 times across a full table — a real screen-reader
 * verbosity regression, not an a11y improvement. Contrast
 * `PartDetailPage.tsx`'s `PartDetailThumb`, which DOES get a real `alt`
 * because it is the page's SOLE visual identifier with no adjacent text.
 *
 * Each of the `shown` slots is decided independently: slot `i` renders
 * `thumbnailUrls[i]` as a real `<img>` when present and loadable, else the
 * existing decorative cube-glyph `<span>` — never a CSS `url()`/
 * `background-image` (D17: React escapes the `src` attribute, so a
 * server-supplied URL can never break out into a style context; the
 * fallback's own CSS mask stays a static stylesheet literal, never
 * data-driven). A 404 flips that one slot to the fallback via `onError`.
 *
 * That failure flip is permanent for the SWATCH INSTANCE, not the render:
 * D5/D6 keep this whole tree mounted across a spend date-range refresh, and
 * `setOrders(data.items)` can hand a slot a different, valid URL afterward
 * (Gate 5 W1). Keying each swatch on its own URL — rather than its array
 * index — forces React to mount a fresh instance (fresh `failed` state)
 * whenever the URL changes, instead of reusing a stale, permanently-failed
 * instance under a new URL.
 */
export function PartThumbnails({ count, thumbnailUrls = [], max = 2 }: PartThumbnailsProps) {
  const shown = Math.max(0, Math.min(count, max))
  const extra = count - shown
  return (
    <span className="order-row-thumbnails" aria-hidden="true">
      {Array.from({ length: shown }, (_, index) => {
        const url = thumbnailUrls[index]
        // Keyed on the URL (not `index`, Gate 5 W1): a refresh that swaps
        // this slot's URL must mount a fresh swatch — with fresh `failed`
        // state — rather than reusing an instance that 404'd under a
        // PREVIOUS url. Empty slots key on the index, which is safe since
        // they render no `failed` state to leak across a refresh.
        return <PartThumbnailSwatch key={url ?? `empty-${index}`} url={url} />
      })}
      {extra > 0 && <span className="order-row-thumbnail-extra">+{extra}</span>}
    </span>
  )
}

/** One swatch slot: a real `<img>` when a URL is present and hasn't 404'd,
 * else the decorative cube-glyph placeholder box. `failed` is local and
 * permanent for this URL — once a URL 404s it never retries — but the
 * PARENT keys this component on the URL itself (Gate 5 W1), so a later
 * refresh that swaps in a different, valid URL mounts a fresh instance
 * with fresh `failed` state rather than reusing this one. */
function PartThumbnailSwatch({ url }: { readonly url: string | undefined }) {
  const [failed, setFailed] = useState(false)
  if (!url || failed) {
    return <span className="order-row-thumbnail" />
  }
  return (
    <img
      className="order-row-thumbnail-img"
      src={url}
      alt=""
      aria-hidden="true"
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  )
}
